import defaultsDeep from 'lodash/defaultsDeep';
import isEqual from 'lodash/isEqual';
import React, { createContext, useContext, useState, useEffect } from 'react';
import reduce from 'lodash/reduce';
import { addParamsToUrl } from '@zapier/url-utils';
import { SplitFactory } from '@splitsoftware/splitio';

/**
 * Borrowed from team Monitor: https://github.com/zapier/reporting/blob/a623f082660c9ee1d1040ee669c5dbc9cd0fd778/packages/reporting/src/utils/buildStaticAssetUrl/buildStaticAssetUrl.ts
 */

const STATIC_ASSET_URL = 'https://cdn.zapier.com/zapier/';

/**
 * Make sure the URL is valid.
 *
 * We make sure to dedupe some URL parts:
 * - The provided `staticPath` is generally suffixed with a `/`.
 * - Some legacy components are still prefixing assets with `/static`.
 */
const sanitizeUrl = url => url.replace(/(\/){2,}/g, '$1').replace(/(https?:\/)/g, '$1/').replace(/(\/static){2,}/g, '$1');

/**
 * Check if a given URL should remain unprefixed.
 */
const shouldRemainUnprefixed = function () {
  let url = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return ['http', 'data:', '/media/storage', 'blob:'].some(beginning => (url || '').indexOf(beginning) === 0);
};
const buildStaticAssetUrl = function (url) {
  let staticPath = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : STATIC_ASSET_URL;
  return shouldRemainUnprefixed(url) ? url : sanitizeUrl(`${staticPath}/${url}`);
};

const isProduction = process.env.NEXT_PUBLIC_VERCEL_ENV === 'production';

// Avoid applying a `max-width` so that we can allow a cascading effect to
// happen, e.g. tablet styles will apply if desktop styles are not provided.
const mediaQueries = {
  mobile: `@media (min-width: 0)`,
  tablet: `@media (min-width: 660px)`,
  desktop: `@media (min-width: 1024px)`
};
const getResponsiveStyles = styles => {
  return [{
    [mediaQueries.mobile]: styles.mobile || {}
  }, {
    [mediaQueries.tablet]: styles.tablet || {}
  }, {
    [mediaQueries.desktop]: styles.desktop || {}
  }];
};
const visibleOnlyOnMobile = getResponsiveStyles({
  mobile: {
    display: 'inherit',
    visibility: 'visible'
  },
  tablet: {
    display: 'none',
    visibility: 'hidden'
  },
  desktop: {
    display: 'none',
    visibility: 'hidden'
  }
});
const visibleOnlyOnTablet = getResponsiveStyles({
  mobile: {
    display: 'none',
    visibility: 'hidden'
  },
  tablet: {
    display: 'inherit',
    visibility: 'visible'
  },
  desktop: {
    display: 'none',
    visibility: 'hidden'
  }
});
const visibleOnlyOnDesktop = getResponsiveStyles({
  mobile: {
    display: 'none',
    visibility: 'hidden'
  },
  tablet: {
    display: 'none',
    visibility: 'hidden'
  },
  desktop: {
    display: 'inherit',
    visibility: 'visible'
  }
});
const hiddenOnMobile = getResponsiveStyles({
  mobile: {
    display: 'none',
    visibility: 'hidden'
  },
  tablet: {
    display: 'inherit',
    visibility: 'visible'
  },
  desktop: {
    display: 'inherit',
    visibility: 'visible'
  }
});
const hiddenOnDesktop = getResponsiveStyles({
  mobile: {
    display: 'inherit',
    visibility: 'visible'
  },
  tablet: {
    display: 'inherit',
    visibility: 'visible'
  },
  desktop: {
    display: 'none',
    visibility: 'hidden'
  }
});

function isBrowser$1() {
  return typeof window !== 'undefined';
}

function isCypress() {
  // @ts-ignore Cypress will be on window if we are in a test
  return isBrowser$1() && window.top?.Cypress !== undefined;
}

function isInIframe() {
  if (!isBrowser$1()) return false;
  try {
    // Do not show cypress tests as in iFrame
    if (
    // @ts-ignore Cypress will be on window if we are in a test
    window.top?.Cypress !== undefined ||
    // @ts-ignore __STORYBOOK_ADDONS will be on window if we are in storybook
    window.top?.__STORYBOOK_ADDONS__ !== undefined) return false;
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

const RE_ABSOLUTE_URL = /^(https?:)?\/\//;

/**
 * Resolve `url` to an absolute URL.
 *
 * It prepends `baseUrl` or falls back to `location.origin` otherwise.
 */
function resolveUrl(url, baseUrl) {
  if (RE_ABSOLUTE_URL.exec(url)) return url;
  const prefix = baseUrl || location.origin;
  const sep = url[0] === '/' ? '' : '/';
  return `${prefix}${sep}${url}`;
}

/** URLS used throughout packages */
const urls = {
  ACCOUNT_DISCOVERY_URL: '/api/v4/account-discovery',
  ACCOUNT_LOCKED_URL: '/app/login/locked',
  ACTIVATE_ACCOUNT_URL: '/app/login/activate',
  COOKIE_ERROR_URL: '/app/login/cookies-disabled',
  COMPANY_PLAN_URL: '/companies',
  TEAMS_COMPANIES_PLAN_URL: '/teams-companies',
  CSRF_URL: '/api/v3/csrf',
  CURRENT_SESSION_URL: '/api/v4/session',
  DEFAULT_LOGIN_NEXT: '/app/home',
  DEFAULT_SIGNUP_NEXT: '/app/onboarding',
  DOMAIN_DISCOVERY_URL: '/api/v4/domain-discovery',
  ENTERPRISE_PLAN_URL: '/enterprise',
  IFRAME_DEFAULT_SIGNUP_NEXT: '/app/home',
  FORGOT_PASSWORD_URL: '/app/login/forgot',
  FORGOT_PASSWORD_API_URL: '/api/v3/accounts/password/forgot',
  HELP_URL: '/app/get-help',
  INDEX_URL: '/',
  INVITATION_API_URL: '/api/v4/invitations',
  LEGAL_URL: '/legal',
  LOGIN_API_URL: '/api/v3/login',
  LOGIN_URL: '/app/login',
  LOGIN_WITH_SSO_URL: '/app/login/sso',
  LOGOUT_API_URL: '/api/logout',
  MFA_URL: '/app/login/2fa',
  PRIVACY_URL: '/privacy',
  RESET_PASSWORD_API_URL: '/api/v3/accounts/password/reset',
  RESET_PASSWORD_URL: '/app/login/reset-password',
  SIGNUP_API_URL: '/api/v3/profiles',
  SIGNUP_URL: '/sign-up',
  SINGLE_SESSION_URL: '/app/login/secure-session',
  TWO_FACTOR_AUTH_API_URL: '/api/v3/2fa',
  TWO_FACTOR_URL: '/app/login/2fa',
  ZAP_TEMPLATE_API_URL: '/api/v4/guided-recipes',
  SECURITY_HEADERS_URL: '/api/v4/security/domain/headers/',
  REDIRECT_URL: '/redirect'
};

// Export parseQuery for testing
const parseQuery = query => {
  // Only works for `and` queries for now.
  const parts = query.split('and').map(part => part.trim())
  // Remove parentheses
  .map(part => part.replace(/[()]/g, ''));
  return parts.reduce((accum, part) => {
    const [key = '', val = ''] = part.split(':');
    accum[key.trim()] = parseInt(val, 10);
    return accum;
  }, {});
};

// Export doesQueryMatch for testing if needed
const doesQueryMatch = (queryables, query) => {
  return [queryables.width >= (query['min-width'] || 0), queryables.width <= (query['max-width'] || Infinity)].every(Boolean);
};
const findMatchingQueries = (queryables, queries) => {
  return reduce(queries, (accum, queryToCheck) => {
    const mediaQuery = queryToCheck;
    const parsedQuery = parseQuery(mediaQuery);
    accum[queryToCheck] = doesQueryMatch(queryables, parsedQuery);
    return accum;
  }, {});
};
var findMatchingQueries$1 = findMatchingQueries;

const DEVICE_TYPES = {
  phone: 'phone',
  phablet: 'phablet',
  tablet: 'tablet',
  desktop: 'desktop'
};
const containsAnyUa = substrings => ua => {
  // Load testing tools such as ab and autocannon, and Datadog Synthetics
  // (simulated user requests), do not send a user agent string.
  if (typeof ua !== 'string') {
    return false;
  }
  const normalizedUa = ua.toLowerCase();
  return substrings.map(substring => substring.toLowerCase()).some(substring => normalizedUa.includes(substring));
};
const isTablet = containsAnyUa(['ipad', 'kfapwi']);
const isPhablet = containsAnyUa(['nexus']);
const isMobile = containsAnyUa(['iphone', 'mobile']);
const guessUserAgentInfo = userAgent => {
  // Order is important since subsequent conditions are more generic.
  // Specifically, guessing a tablet looks for specific devices,
  // as does phablet, and then mobile looks for devices or a `mobile`
  // string in the UA. Desktop is the fallback.
  if (isTablet(userAgent)) {
    return {
      deviceType: DEVICE_TYPES.tablet,
      width: 660
    };
  }
  if (isPhablet(userAgent)) {
    return {
      deviceType: DEVICE_TYPES.phablet,
      width: 420
    };
  }
  if (isMobile(userAgent)) {
    return {
      deviceType: DEVICE_TYPES.phone,
      width: 320
    };
  }
  return {
    deviceType: DEVICE_TYPES.desktop,
    width: 1024
  };
};
var guessUserAgentInfo$1 = guessUserAgentInfo;

const isBrowser = () => typeof window !== 'undefined';
const userAgent = isBrowser() ? navigator.userAgent : '';
const UserAgentContext = /*#__PURE__*/createContext(userAgent);

const mergeProps = propsArray => {
  // `reverse` because this is `defaultsDeep`, so earlier
  // items in the array take precedence, but we want later
  // items to take precedence.
  return defaultsDeep({}, ...propsArray.reverse());
};
const computeInitialPropsFromUserAgent = (userAgent, mediaQueriesToProps) => {
  const userAgentInfo = guessUserAgentInfo$1(userAgent);
  const mediaQueries = Object.keys(mediaQueriesToProps);
  const matchingQueries = findMatchingQueries$1(userAgentInfo, mediaQueries);
  const matchingProps = mediaQueries.filter(query => matchingQueries[query]).map(query => mediaQueriesToProps[query]);
  return mergeProps(matchingProps);
};
const mergeMatchingProps = queryObjs => {
  const matchingProps = queryObjs.filter(queryObj => queryObj?.mediaQueryList?.matches).map(queryObj => queryObj.props);
  return mergeProps(matchingProps);
};

// Hook that takes a `mediaQueriesToProps` which is an object whose
// keys are media queries and whose values are objects
// representing props of one or more components. Returns
// a deeply merged object of all props matching the media
// queries defined in `mediaQueriesToProps`.
const useMediaProps = mediaQueriesToProps => {
  const userAgent = useContext(UserAgentContext);
  const initialProps = computeInitialPropsFromUserAgent(userAgent, mediaQueriesToProps);
  const [props, setProps] = useState(initialProps);
  useEffect(() => {
    const mediaQueries = Object.keys(mediaQueriesToProps);
    // Hold onto `query`, `mediaQueryList`, and `props` since `matchMedia`
    // may change how `query` is structured and we need a way
    // to easily reaccess the `props` defined for that query.
    const queryObjs = mediaQueries.map(query => {
      return {
        query,
        mediaQueryList: window?.matchMedia(query),
        props: mediaQueriesToProps[query]
      };
    });
    // Merge all the props for matching media queries together
    // and then set them as the new props.
    const computeProps = () => {
      const newProps = mergeMatchingProps(queryObjs);
      // Perf enhancement / infinite loop prevention.
      if (!isEqual(newProps, props)) {
        setProps(newProps);
      }
    };
    // Listen to changes for each of the media queries.
    // These events only fire when a media query matches,
    // unlike `onresize`.
    queryObjs.forEach(queryObj => {
      if (queryObj?.mediaQueryList?.addListener) {
        queryObj.mediaQueryList.addListener(computeProps);
      }
    });
    // Initial call to compute the props. Without this
    // the props would never be computed unless the user
    // resized their browser to match a media query.
    computeProps();
    // Return the teardown function.
    return () => {
      queryObjs.forEach(queryObj => {
        if (queryObj?.mediaQueryList?.removeListener) {
          queryObj.mediaQueryList.removeListener(computeProps);
        }
      });
    };
  }, [mediaQueriesToProps, props]);
  return props;
};
var useMediaProps$1 = useMediaProps;

const queries = {
  desktop: `(min-width: 1024px)`,
  tablet: `(min-width: 660px) and (max-width: 1023px)`
};
function useSize() {
  return useMediaProps$1({
    all: {
      size: 'small'
    },
    [queries.tablet]: {
      size: 'medium'
    },
    [queries.desktop]: {
      size: 'large'
    }
  }).size;
}

function resolveSignUpNextUrl() {
  let nextUrl = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : undefined;
  let params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let context = arguments.length > 2 ? arguments[2] : undefined;
  if (isInIframe()) {
    return nextUrl || urls.IFRAME_DEFAULT_SIGNUP_NEXT;
  }
  // prevent app/onboarding to be appended twice
  if (nextUrl?.includes('onboarding')) {
    return nextUrl;
  }
  const nextQueryParams = {
    next: nextUrl,
    selected_apis: params.selected_apis,
    invitation_code: params.invitation_code,
    from: params.from
  };
  let baseUrl = urls.DEFAULT_SIGNUP_NEXT;
  if (context?.skipOnboarding) {
    baseUrl = urls.REDIRECT_URL;
  }

  // add only truthy params
  return addParamsToUrl(baseUrl, nextQueryParams, true);
}

// Persist the client so we don't have to keep initializing on repeat calls
let splitInstance;

/**
 * Returns an instance of the Split SDK.
 */
function getServerSideSplitSingleton() {
  // Configuration
  const SPLIT_READY_TIMEOUT = process.env.SPLIT_READY_TIMEOUT ? parseInt(process.env.SPLIT_READY_TIMEOUT) : 5;
  const NEXT_PUBLIC_SPLIT_API_KEY = process.env.NEXT_PUBLIC_SPLIT_API_KEY;
  const SPLIT_FEATURES_PATH = process.env.SPLIT_FEATURES_PATH;
  const SPLIT_DEBUG = process.env.SPLIT_DEBUG?.toLowerCase() === 'true';
  if (!NEXT_PUBLIC_SPLIT_API_KEY) {
    throw new Error('Missing configuration for NEXT_PUBLIC_SPLIT_API_KEY.');
  }
  if (isBrowser$1()) {
    throw new Error('getServerSideSplitSingleton() should only be used server-side.');
  }
  if (!splitInstance) {
    splitInstance = SplitFactory({
      core: {
        authorizationKey: NEXT_PUBLIC_SPLIT_API_KEY
      },
      features: SPLIT_FEATURES_PATH !== '' ? SPLIT_FEATURES_PATH : undefined,
      startup: {
        readyTimeout: SPLIT_READY_TIMEOUT
      },
      debug: SPLIT_DEBUG
    });
  }
  return splitInstance;
}

/**
 * Returns an identifier to pass as the "key" to Split SDK impressions.
 * In order:
 * - splitId query param: used for QA testing since these pages are unathenticated
 * - visitor_id cookie: Set automatically by Cloudfront function in staging/production.
 * - '0': Default value
 * @param context
 * @returns {String}
 */
function getServerSideSplitId(context) {
  return String(context.query.splitId || context.req.cookies['visitor_id'] || '0');
}

const FeatureFlagContext = /*#__PURE__*/createContext({
  treatments: {}
});

/**
 * Returns the active treatment for a specific feature flag.
 */
const useFeatureFlag = featureName => {
  const context = useContext(FeatureFlagContext);
  return context.treatments[featureName];
};

/**
 * Provider to access treatments within child components.
 */
const FeatureFlagProvider = _ref => {
  let {
    treatments,
    children
  } = _ref;
  return children ? /*#__PURE__*/React.createElement(FeatureFlagContext.Provider, {
    value: {
      treatments
    }
  }, children) : null;
};

const FEATURE_FLAGS = {
  NLA_EMBED_REDIRECT: 'nla_embed_redirect',
  COMPANY_SALES_REDIRECT: 'experiment_company-card-contact-sales_zapier',
  TPS_EXPERIMENT_SIGNUP_LANDING_PAGE: 'tps_experiment_signup_landing_page'
};

function isNLAPluginSignUp(utmCampaign) {
  return Array.isArray(utmCampaign) ? utmCampaign[0] === 'openai' : utmCampaign === 'openai';
}

export { FEATURE_FLAGS, FeatureFlagProvider, STATIC_ASSET_URL, buildStaticAssetUrl, getResponsiveStyles, getServerSideSplitId, getServerSideSplitSingleton, hiddenOnDesktop, hiddenOnMobile, isBrowser$1 as isBrowser, isCypress, isInIframe, isNLAPluginSignUp, isProduction, mediaQueries, resolveSignUpNextUrl, resolveUrl, urls, useFeatureFlag, useSize, visibleOnlyOnDesktop, visibleOnlyOnMobile, visibleOnlyOnTablet };
